import {Autocomplete, Box, Divider, Stack, TextField, Typography} from "@mui/material";
import React, {ReactElement} from "react";
import {useTranslation} from "react-i18next";
import CColorPicker from "crqlar-react-common/dist/components/CColorPicker"
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {useCurrentApp} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import RatesList from "./RatesList";
import {
    useAllResourceCategoriesForSector
} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useAllRootResourcesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import ConfirmationSettingsSection from "../../system/newsletterAccount/ConfirmationSettingsSection";

interface Props {
    service: Service
    setService: (service: Service) => void
}

const NewServiceCommonData = ({service, setService}: Props) => {

    const {t, i18n} = useTranslation();
    const appData = useCurrentApp()
    const sectorId = appData?.configuration?.sectorId
    const sector = useSector(sectorId)
    const resourceCategories = useAllResourceCategoriesForSector(sectorId)
    const allResources = useAllRootResourcesForSector(sectorId)
    const ageCategories = useAllAgeCategories()

    const updateField = (field: keyof Service, value: any) => {
        setService({...service, [field]: value})
    }

    let sectorSpecificUI: ReactElement | null = null

    switch (sector?.sectorType) {
        case SectorType.RESTAURANT:
            sectorSpecificUI = null
            break;
        default:
            sectorSpecificUI = (
                <>
                    <Stack
                        direction={'row'}
                        spacing={2}
                        alignItems={'center'}
                    >

                    </Stack>
                </>
            )
            break;
    }

    return (
        <Stack sx={{
            gap: 2,
            p: 1
        }}>
            <Typography variant={'h6'}>
                {t('General info')}
            </Typography>

            <Stack
                sx={{
                    width: '100%',
                    gap: 2
                }}
                direction={'row'}
            >
                <TextField
                    sx={{
                        width: '50%'
                    }}
                    label={t('Name')}
                    value={service.name || ''}
                    onChange={(e) => {
                        updateField('name', e.target.value)
                    }}
                    error={!service.name?.trim()}
                />

                <TextField
                    sx={{
                        width: '50%'
                    }}
                    id={'duration'}
                    label={t('Duration in minutes')}
                    value={service.durationMinutes ?? ''}
                    onChange={(e) => updateField('durationMinutes', parseInt(e.target.value))}
                    type={'number'}
                />

            </Stack>

            <Typography variant={'h6'}>
                {t('Linked resources')}
            </Typography>

            <Stack
                direction={'row'}
                gap={2}
            >
                {
                    resourceCategories?.map(category => {
                        const resources = allResources.filter(r => r.categoryIds?.includes(category.id!))
                        return (
                            <Autocomplete
                                key={category.id}
                                id={`resources-${category.name}`}
                                multiple
                                options={resources}
                                getOptionLabel={(option: any) => option.name || ''}
                                value={service.resourceIds?.map(id => resources.find(c => c.id === id)).filter(Boolean) as any[] || []}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        label={category.name}
                                    />
                                )}
                                fullWidth
                                onChange={(e, value) => {
                                    // Extract only old resource ids for this category
                                    const oldResourceIds = service.resourceIds?.filter(id => {
                                        const resource = allResources.find(r => r.id === id)
                                        return resource?.categoryIds?.includes(category.id!)
                                    }) || []

                                    // Remove old resource ids for this category
                                    const newResourceIds = service.resourceIds?.filter(id => !oldResourceIds.includes(id)) || []

                                    // Add new resource ids for this category
                                    updateField('resourceIds', [...newResourceIds, ...value.map((v: any) => v.id)])
                                }}
                            />
                        )
                    })
                }
            </Stack>


            <RatesList setService={setService} service={service}/>

            <Box height={12}/>

            <AdditionalProperties
                title={t('Translations')}
                variant={'outlined'}
            >

                <LocalizedTextField
                    id={'name-localized'}
                    size={'small'}
                    label={t('Name')}
                    value={service.nameLocalized}
                    onChange={(e) => {
                        updateField('nameLocalized', e)
                    }}
                    fullWidth
                />

                <Divider/>

                <LocalizedTextField
                    id={'description-localized'}
                    size={'small'}
                    label={t('Description')}
                    value={service.descriptionLocalized}
                    onChange={(e) => {
                        updateField('descriptionLocalized', e)
                    }}
                    fullWidth
                    multiline
                    rows={3}
                />
            </AdditionalProperties>


            <AdditionalProperties
                title={t('More')}
                variant={'outlined'}
            >
                <CColorPicker
                    value={(service.meta as any)?.tagColor}
                    onChange={(e) => {
                        updateField('meta', {
                            ...service.meta,
                            tagColor: e
                        })
                    }}
                    sx={{
                        width: '50%'
                    }}
                />

                <Typography mt={2} mb={1}>
                    {t('Required resources')}
                </Typography>

                {
                    resourceCategories.map(r => {
                        return (
                            <Stack
                                direction={'row'}
                                key={r.id}
                                width={'100%'}
                                gap={1}
                            >
                                <CountTextField
                                    label={r.name}
                                    value={service.assignedResourcesRequirementPerCategory?.[r.id!] || ''}
                                    onChangeValue={(e) => {
                                        const newAssignements = {
                                            ...service.assignedResourcesRequirementPerCategory,
                                            [r.id!]: e
                                        }
                                        updateField('assignedResourcesRequirementPerCategory', newAssignements)
                                    }}
                                    fullWidth
                                />

                                <CountTextField
                                    label={t(`Duration override for`, {name: r.name})}
                                    value={service.resourceCategoryDurationMinutes?.[r.id!] || ''}
                                    onChangeValue={(e) => {
                                        const newAssignements = {
                                            ...service.resourceCategoryDurationMinutes,
                                            [r.id!]: e
                                        }
                                        updateField('resourceCategoryDurationMinutes', newAssignements)
                                    }}
                                    fullWidth
                                />

                            </Stack>
                        )
                    })
                }

                <Typography mt={2} mb={1}>
                    {t('Age categories')}
                </Typography>

                <Autocomplete
                    id={'age-categories'}
                    multiple
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={service.ageCategoryIds?.map(id => ageCategories.find(c => c.id === id)).filter(Boolean) as AgeCategory[] || []}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            label={t('Age categories')}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('ageCategoryIds', value.map((v: any) => v.id))
                    }}
                />

            </AdditionalProperties>

            {sectorSpecificUI}


        </Stack>
    )

}
export default NewServiceCommonData;
